import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { http, formatError } from "@/lib/api";
import { toast } from "sonner";
import ETransferPanel from "@/components/ETransferPanel";

const TIME_SLOTS = ["18:00", "19:00", "20:00", "21:00", "22:00", "23:00"];

export default function Contact() {
  const [params] = useSearchParams();
  const [rates, setRates] = useState([]);
  const [site, setSite] = useState(null);
  const [rateId, setRateId] = useState(params.get("rate") || "");
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [contactSent, setContactSent] = useState(false);
  const [etransfer, setEtransfer] = useState(null); // holds created booking after submit

  useEffect(() => {
    http.get("/rates").then((r) => {
      setRates(r.data);
      if (!rateId && r.data.length) setRateId(r.data[0].id);
    });
    http.get("/site").then((r) => setSite(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedRate = rates.find((r) => r.id === rateId);

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!rateId || !date || !time) {
      toast.error("Please pick a date, time and service.");
      return;
    }
    setSubmitting(true);
    try {
      const iso = date.toISOString().slice(0, 10);
      const { data: booking } = await http.post("/bookings", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        date: iso,
        time,
        rate_id: rateId,
        notes: form.notes,
        origin_url: window.location.origin,
      });
      setEtransfer(booking);
      toast.success("Your booking is reserved. Follow the e-Transfer instructions to lock it in.");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    try {
      await http.post("/contact", contactForm);
      setContactSent(true);
      toast.success("Message sent. I'll be in touch soon.");
      setContactForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div className="pt-32 pb-24" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
        <div className="mb-12">
          <div className="text-[10px] tracking-[0.35em] uppercase text-pink-blush mb-3">Reserve & Reach out</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-champagne leading-tight">
            Let's meet, <span className="italic text-pink-hot">beautiful</span>
          </h1>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left: contact info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-4 space-y-6"
          >
            <div className="glass-panel p-8 rounded-md">
              <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-5">Contact</div>
              <div className="space-y-4 text-sm text-champagne/85">
                <div className="flex gap-3 items-center"><Mail className="w-4 h-4 text-pink-hot" /> {site?.contact_email}</div>
                <div className="flex gap-3 items-center"><Phone className="w-4 h-4 text-pink-hot" /> {site?.contact_phone}</div>
                <div className="flex gap-3 items-center"><MapPin className="w-4 h-4 text-pink-hot" /> Miami · New York · Private Travel</div>
                <div className="flex gap-3 items-center"><Clock className="w-4 h-4 text-pink-hot" /> Evenings & Weekends (by appointment)</div>
              </div>
            </div>

            <div className="glass-panel p-8 rounded-md">
              <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-3">Policy</div>
              <p className="text-sm text-champagne/75 leading-relaxed">
                {site?.booking_policy}
              </p>
            </div>

            <form onSubmit={submitContact} className="glass-panel p-8 rounded-md space-y-3" data-testid="contact-form">
              <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-1">General Inquiry</div>
              <input
                required placeholder="Name" value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                className="w-full bg-noir-base border border-rosegold/20 text-champagne px-4 py-3 text-sm rounded-sm focus:border-pink-hot focus:outline-none"
                data-testid="contact-name"
              />
              <input
                required type="email" placeholder="Email" value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                className="w-full bg-noir-base border border-rosegold/20 text-champagne px-4 py-3 text-sm rounded-sm focus:border-pink-hot focus:outline-none"
                data-testid="contact-email"
              />
              <textarea
                required placeholder="Message" rows={3} value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                className="w-full bg-noir-base border border-rosegold/20 text-champagne px-4 py-3 text-sm rounded-sm focus:border-pink-hot focus:outline-none"
                data-testid="contact-message"
              />
              <button
                disabled={contactSent}
                className="w-full py-3 bg-pink-hot text-white text-xs tracking-[0.28em] uppercase rounded-sm hover:bg-pink-glow transition-all disabled:opacity-50"
                data-testid="contact-submit"
              >
                {contactSent ? "Sent" : "Send Message"}
              </button>
            </form>
          </motion.div>

          {/* Right: booking flow */}
          <motion.form
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            onSubmit={submitBooking}
            className="lg:col-span-8 glass-panel rounded-md p-8 sm:p-10 space-y-8"
            data-testid="booking-form"
          >
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-3">Step 1 · Choose your experience</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {rates.map((r) => (
                  <button
                    type="button"
                    key={r.id}
                    onClick={() => setRateId(r.id)}
                    data-testid={`select-rate-${r.id}`}
                    className={`text-left p-5 rounded-sm border transition-all ${
                      rateId === r.id
                        ? "border-pink-hot bg-pink-hot/10 glow-pink"
                        : "border-rosegold/15 bg-noir-base hover:border-pink-hot/40"
                    }`}
                  >
                    <div className="text-[10px] tracking-[0.25em] uppercase text-rosegold">{r.duration}</div>
                    <div className="font-display text-2xl italic text-champagne mt-1">{r.title}</div>
                    <div className="flex justify-between items-end mt-2">
                      <div className="text-sm text-champagne/70">Deposit ${r.deposit}</div>
                      <div className="font-display text-2xl text-pink-hot">${r.price}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-3">Step 2 · Select a date</div>
                <div className="rounded-sm border border-pink-hot/20 p-3 bg-noir-base inline-block">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    data-testid="booking-calendar"
                  />
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-3">Step 3 · Pick a time</div>
                <div className="grid grid-cols-3 gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setTime(t)}
                      data-testid={`slot-${t}`}
                      className={`py-3 text-sm tracking-wider rounded-sm border transition-all ${
                        time === t
                          ? "bg-pink-hot text-white border-pink-hot glow-pink"
                          : "border-rosegold/20 text-champagne/80 hover:border-pink-hot/50"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                {date && time && (
                  <div className="mt-5 text-sm text-champagne/80">
                    <span className="font-display italic text-pink-hot">Reserved:</span>{" "}
                    {date.toDateString()} at {time}
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-3">Step 4 · Your details</div>
              <div className="grid md:grid-cols-2 gap-3">
                <input required placeholder="Full name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-noir-base border border-rosegold/20 px-4 py-3 text-sm rounded-sm focus:border-pink-hot focus:outline-none"
                  data-testid="booking-name" />
                <input required placeholder="Phone" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-noir-base border border-rosegold/20 px-4 py-3 text-sm rounded-sm focus:border-pink-hot focus:outline-none"
                  data-testid="booking-phone" />
                <input required type="email" placeholder="Email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="md:col-span-2 bg-noir-base border border-rosegold/20 px-4 py-3 text-sm rounded-sm focus:border-pink-hot focus:outline-none"
                  data-testid="booking-email" />
                <textarea rows={3} placeholder="Notes (references, preferences)..." value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="md:col-span-2 bg-noir-base border border-rosegold/20 px-4 py-3 text-sm rounded-sm focus:border-pink-hot focus:outline-none"
                  data-testid="booking-notes" />
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 pt-4 border-t border-pink-hot/10 flex-wrap">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-rosegold">Due now (non-refundable)</div>
                <div className="font-display text-4xl text-pink-hot">${selectedRate?.deposit ?? 0}</div>
                <div className="text-xs text-champagne/60 mt-1">Paid by Interac e-Transfer · Balance ${((selectedRate?.price || 0) - (selectedRate?.deposit || 0))} in person</div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="px-10 py-4 bg-pink-hot text-white text-xs tracking-[0.3em] uppercase rounded-sm hover:bg-pink-glow glow-pink transition-all disabled:opacity-60"
                data-testid="booking-submit"
              >
                {submitting ? "Reserving..." : "Reserve & See E-Transfer Details"}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
      {etransfer && (
        <ETransferPanel
          payment={{
            type: "booking",
            amount: etransfer.deposit_amount,
            client_email: etransfer.email,
            reference_id: etransfer.id,
            balance_due: (etransfer.total_amount || 0) - (etransfer.deposit_amount || 0),
            details: `${etransfer.rate_title} · ${etransfer.date} at ${etransfer.time}`,
          }}
          site={site}
          onClose={() => {
            setEtransfer(null);
            setDate(undefined);
            setTime("");
            setForm({ name: "", email: "", phone: "", notes: "" });
          }}
        />
      )}
    </div>
  );
}
